import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowUpRight,
  BellRing,
  Check,
  Download,
  Hourglass,
  Loader2,
  SearchX,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ROUTE } from '@/config';
import { ExportModalView } from '@/hooks/useExportModalView';
import { exportDelivery, formatExportCount } from '@/utils/helpers/orderExport';

interface ExportFooterProps {
  view: ExportModalView;
}

/** The live summary: big count, file format, how it will be delivered. */
function ExportSummary({ view }: ExportFooterProps) {
  const { t, i18n } = useTranslation();
  const { count, isCounting, isEmpty } = view.state;

  if (isEmpty) {
    return (
      <div className='flex items-center gap-2 rounded-xl bg-sd-muted px-3 py-2.5 text-sm text-sd-foreground'>
        <SearchX className='h-4 w-4 shrink-0 text-sd-muted-foreground' aria-hidden />
        <span>{String(t('export_modal.empty'))}</span>
      </div>
    );
  }
  if (!isCounting && count === undefined) {
    return (
      <p className='m-0 text-sm text-sd-muted-foreground'>
        {String(t('export_modal.count_unknown'))}
      </p>
    );
  }

  const isAsync = count !== undefined && exportDelivery(count) === 'async';
  const HintIcon = isAsync ? Hourglass : Zap;

  return (
    <div className='flex flex-col gap-0.5'>
      <div className='flex items-baseline gap-1.5'>
        {isCounting || count === undefined ? (
          <>
            <Skeleton aria-hidden className='h-6 w-16 self-center rounded-md bg-sd-muted' />
            <span className='sr-only'>{String(t('export_modal.counting'))}</span>
          </>
        ) : (
          <span className='text-2xl font-bold leading-7 tracking-tight text-sd-foreground'>
            {formatExportCount(count, i18n.language)}
          </span>
        )}
        <span className='text-sm font-medium text-sd-muted-foreground'>
          {String(t('export_modal.orders_word'))}
        </span>
      </div>
      <span className='text-xs text-sd-muted-foreground'>
        {String(t('export_modal.format_line'))}
      </span>
      {isCounting ? null : (
        <span className='mt-1 flex items-start gap-1.5 text-xs font-medium text-sd-foreground'>
          <HintIcon className='mt-px h-3.5 w-3.5 shrink-0 text-amber-500' aria-hidden />
          {String(t(isAsync ? 'export_modal.hint_async' : 'export_modal.hint_sync'))}
        </span>
      )}
    </div>
  );
}

/** The primary action; it carries the export's progress and outcome. */
function PrimaryAction({ view }: ExportFooterProps) {
  const { t, i18n } = useTranslation();
  const { state, phase } = view;

  if (phase === 'done' || phase === 'queued') {
    const Icon = phase === 'done' ? Check : BellRing;
    return (
      <Button type='button' onClick={view.dismiss} className='h-10 rounded-xl px-4 font-semibold'>
        <Icon aria-hidden />
        {String(t(phase === 'done' ? 'export_modal.done' : 'export_modal.notify'))}
      </Button>
    );
  }

  return (
    <Button
      type='button'
      onClick={view.exportNow}
      disabled={!state.canExport}
      className='h-10 rounded-xl px-4 font-semibold'
    >
      {state.isExporting ? (
        <Loader2 className='animate-spin' aria-hidden />
      ) : (
        <Download aria-hidden />
      )}
      {state.isExporting
        ? String(t('export_modal.preparing_short'))
        : state.count
          ? String(t('export_modal.export_n', { n: formatExportCount(state.count, i18n.language) }))
          : String(t('export_modal.export_csv'))}
    </Button>
  );
}

/** BOXY-409 — sticky footer: summary (aria-live), errors inline, actions. */
export function ExportFooter({ view }: ExportFooterProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openFiles = () => {
    view.dismiss();
    navigate(ROUTE.SETTINGS_FILES);
  };

  return (
    <div className='flex flex-col gap-3 border-0 border-t border-solid border-sd-border bg-white px-5 py-4 sm:px-6'>
      {view.phase === 'failed' ? (
        <div
          role='alert'
          className='flex items-start gap-2 rounded-xl border border-solid border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700'
        >
          <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
          <span>{String(t('export_modal.failed'))}</span>
        </div>
      ) : null}
      <div className='flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
        <div role='status' aria-live='polite' className='min-w-0 flex-1'>
          <ExportSummary view={view} />
        </div>
        <div className='flex flex-col items-stretch gap-2 sm:items-end'>
          <div className='flex gap-2 [&>button]:flex-1 sm:[&>button]:flex-none'>
            {view.phase === 'done' || view.phase === 'queued' ? null : (
              <Button
                type='button'
                variant='outline'
                onClick={view.dismiss}
                className='h-10 rounded-xl border-solid px-4'
              >
                {String(t('export_modal.cancel'))}
              </Button>
            )}
            <PrimaryAction view={view} />
          </div>
          {view.phase === 'queued' ? (
            <button
              type='button'
              onClick={openFiles}
              className='inline-flex cursor-pointer items-center justify-center gap-1 border-0 bg-transparent p-0 text-xs font-semibold text-sd-foreground underline underline-offset-2'
            >
              {String(t('export_modal.view_files'))}
              <ArrowUpRight className='h-3.5 w-3.5 rtl:-scale-x-100' aria-hidden />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
